import { Search, X } from 'lucide-react';

export type CatalogStatusFilter = 'all' | 'active' | 'inactive';

interface CatalogToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  statusFilter: CatalogStatusFilter;
  onStatusFilterChange: (value: CatalogStatusFilter) => void;
  /** Visible / filtered count and total, e.g. "12 of 240" */
  visibleCount: number;
  totalCount: number;
  searchPlaceholder?: string;
}

const FILTERS: Array<{ key: CatalogStatusFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
];

/**
 * Search + status filter row shared by all catalog managers.
 * Makes it practical to find duplicates ("Lettuce" vs "Lettuce (LET-001)") in long lists.
 */
export function CatalogToolbar({
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  visibleCount,
  totalCount,
  searchPlaceholder = 'Search…',
}: CatalogToolbarProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
      <div className="relative flex-1 min-w-0">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          type="search"
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-gray-300 focus:outline-none"
        />
        {search && (
          <button
            type="button"
            onClick={() => onSearchChange('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded"
            aria-label="Clear search"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <div
          className="inline-flex rounded-lg border border-gray-200 overflow-hidden text-sm"
          role="group"
          aria-label="Status filter"
        >
          {FILTERS.map(f => (
            <button
              key={f.key}
              type="button"
              onClick={() => onStatusFilterChange(f.key)}
              aria-pressed={statusFilter === f.key}
              className={`px-3 py-1.5 transition-colors ${
                statusFilter === f.key
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-500 whitespace-nowrap tabular-nums">
          {visibleCount === totalCount ? `${totalCount}` : `${visibleCount} of ${totalCount}`}
        </span>
      </div>
    </div>
  );
}
